import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { EmailService } from '../../../services/EmailService';
import { Event } from '../../../services/EventService';
import { supabase } from '../../../config/supabase';
import { AuthModal } from '../../auth/components/AuthModal';
import { slugify } from '../../../utils/slugify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Loader2, CheckCircle, Info, AlertCircle, Calendar, Clock, MapPin, Mail, Users, Check, UserCheck } from 'lucide-react';

interface TokenValidation {
  isValid: boolean;
  email?: string;
  role?: string;
  error?: any;
}

const ManageEventPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [tokenValidation, setTokenValidation] = useState<TokenValidation | null>(null);
  const [eventData, setEventData] = useState<any>(null);
  const [processing, setProcessing] = useState(false);
  const [linkAttempted, setLinkAttempted] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const token = searchParams.get('token');

  // Reemplazamos validateTokenAndLoadEvent + handleAutoLink por initInvitationFlow
  const initInvitationFlow = async () => {
    try {
      setLoading(true)
      console.log('Validating token...', { token, eventId })
      const validation = await EmailService.verifyManagerToken(token!, eventId!)
      console.log('Token validation result:', validation)
      setTokenValidation(validation)

      if (!validation.isValid) {
        setMessage({ type: 'error', text: 'Invalid or expired invitation link' })
        return
      }

      console.log('Loading event data...')
      const { data: event, error: eventError } = await Event.getById(eventId!)
      console.log('Event data result:', { event, eventError })
      if (eventError || !event) {
        setMessage({ type: 'error', text: 'Event not found' })
        return
      }
      setEventData(event)

      // Aquí, tras tener token + evento, hacemos el auto-link o abrimos el modal
      if (user) {
        setShowAuthModal(false)
        console.log('User existe: aceptando invitación…')
        setProcessing(true)
        setLinkAttempted(true)

        // Aceptar invitación
        const { error } = await Event.acceptInvitation(
          eventId!,
          user.email!,
          { id: user.id, name: user.user_metadata?.full_name, avatar_url: user.user_metadata?.avatar_url }
        )
        if (error && !error.message?.includes('already confirmed')) {
          setMessage({ type: 'error', text: 'Error linking you: ' + error.message })
          return
        }
        setMessage({
          type: 'success',
          text: error
            ? `Already confirmed!`
            : `Confirmed your attendance for ${event.title}!`
        })

        // Marcar token usado
        await supabase
          .from('invitation_tokens')
          .update({ status: 'used', used_at: new Date().toISOString() })
          .eq('token', token!)

        // Redirigir
        setTimeout(() => window.location.href = `/events/${eventId}`, 3000)
      } else {
        console.log('No hay user: abriendo modal')
        setShowAuthModal(true);
      }
    } catch (err) {
      console.error(err)
      setMessage({ type: 'error', text: 'Error procesando la invitación' })
    } finally {
      setLoading(false)
      setProcessing(false)
    }
  }

  // Único useEffect que vuelve a ejecutarse si cambia user, token o eventId
  useEffect(() => {
    if (!eventId || !token) {
      setMessage({ type: 'error', text: 'Invalid link – missing params' })
      setLoading(false)
      return
    }
    initInvitationFlow()
  }, [eventId, token, user])

  const handleAuthSuccess = () => {
    // Auth modal will close automatically via useEffect when user changes
    console.log('Auth successful, waiting for user state update...');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600 font-medium">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600 font-medium">Confirming your management access...</p>
        </div>
      </div>
    );
  }

  if (message?.type === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg p-4">
        <Card className="max-w-md w-full text-center border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-3">Success!</h2>
            <p className="text-green-700 mb-6 text-base">{message.text}</p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Redirecting to event page...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValidation?.isValid || !eventData) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-soft-bg p-4">
        <Card className="max-w-md w-full text-center border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-800 mb-3">Invalid Invitation</h2>
            <p className="text-red-700 mb-6">
              {message?.text || 'This invitation link is invalid or has expired.'}
            </p>
            <div className="space-y-2">
              {eventData && (
                <Button
                  onClick={() => navigate(`/events/${slugify(eventData.title)}/${eventId}`)}
                  className="w-full"
                >
                  View Event Details
                </Button>
              )}
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
              >
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main invitation page - show event details and auth options
  return (
    <div className="min-h-screen brand-soft-bg">
      <div className="max-w-2xl mx-auto py-12 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <UserCheck className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Manager Invitation</h1>
          <p className="text-xl text-gray-600">
            You've been invited to manage attendance for this event
          </p>
        </div>

        {/* Event Card */}
        <Card className="mb-8 border-0 shadow-lg overflow-hidden">
          {eventData.image_url && (
            <div className="h-48 bg-gradient-to-r from-blue-500 to-purple-600 relative">
              <img 
                src={eventData.image_url} 
                alt={eventData.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20" />
            </div>
          )}
          
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-gray-900">{eventData.title}</CardTitle>
            {eventData.short_description && (
              <CardDescription className="text-base text-gray-600 mt-2">
                {eventData.short_description}
              </CardDescription>
            )}
          </CardHeader>
          
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 text-gray-600">
                <Calendar className="w-5 h-5 text-blue-500" />
                <span className="font-medium">
                  {new Date(eventData.start_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
              
              <div className="flex items-center space-x-3 text-gray-600">
                <Clock className="w-5 h-5 text-blue-500" />
                <span className="font-medium">{eventData.start_time}</span>
              </div>
              
              {eventData.location && (
                <div className="flex items-center space-x-3 text-gray-600">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span className="font-medium">{eventData.location}</span>
                </div>
              )}
              
              <div className="flex items-center space-x-3 text-gray-600">
                <Mail className="w-5 h-5 text-blue-500" />
                <span className="font-medium">Invited as: {tokenValidation.email}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 pt-2">
              <UserCheck className="w-5 h-5 text-purple-500" />
              <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-200">
                {tokenValidation.role} Role
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Invitation Details */}
        <Card className="mb-8 border-0 shadow-lg bg-gradient-to-r from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-blue-900">
              What you can do as a {tokenValidation.role}:
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-blue-800">
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>View attendee list and registration details</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Mark attendance during the event</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Approve or manage pending registrations</span>
              </li>
              <li className="flex items-center space-x-2">
                <Check className="w-4 h-4 text-blue-600" />
                <span>Send follow-up communications to attendees</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Message Display */}
        {message && message.type === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-red-700">{message.text}</p>
            </div>
          </div>
        )}

        {/* Auth Modal */}
        <AuthModal
          skipRedirect={true}
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultEmail={tokenValidation.email}
          onSuccess={handleAuthSuccess}
          title="Sign in to accept invitation"
          subtitle={`Use the email ${tokenValidation.email} to accept this invitation`}
          redirectGoogleUri={`${window.location.origin}/events/${eventId}/manage?token=${token}`}
        />
      </div>
    </div>
  );
};

export default ManageEventPage;